// The staff intake wizard: identity, description, opposing parties,
// review. Four steps from a name to a matter, conflict-checked on the
// way in and re-checked at the moment of commitment - the acknowledge
// box lives on the review step because a waiver made against an older
// check could wave through a critical the operator never saw.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { languageName } from "../../../lib/intakeLanguages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  checkConflicts,
  classifyDescription,
  createIntakeMatter,
  listIntakeTemplates,
  type ClassificationResult,
  type ConflictCandidate,
  type ConflictCheckInput,
  type CreateIntakeResponse,
} from "../api/intake";
import { matterRouteId } from "../../../api/mock";
import { ConflictSection } from "../components/ConflictSection";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";

const STEPS = ["Client identity", "Matter description", "Opposing parties", "Review & create"];

// Keyboard-ASCII source: the check mark is built, not typed.
const CHECK = String.fromCharCode(10003);

const MAX_OPPOSING = 20;

const LANGUAGES = ["en", "es", "ru", "uk", "ar", "zh", "pt", "fr"];

const MATCH_WORDS: Record<string, string> = {
  alienNumber: "matched by A-number",
  phone: "matched by phone",
  email: "matched by email",
  "name+dob": "matched by name and date of birth",
};

interface Identity {
  firstName: string;
  lastName: string;
  alienNumber: string;
  dateOfBirth: string;
  primaryPhone: string;
  email: string;
  preferredLanguage: string;
}

const EMPTY_IDENTITY: Identity = {
  firstName: "",
  lastName: "",
  alienNumber: "",
  dateOfBirth: "",
  primaryPhone: "",
  email: "",
  preferredLanguage: "",
};

/** Only typed values travel - an empty string would fail the server's
    dateOfBirth refine and add noise everywhere else. */
function conflictPayload(identity: Identity, opposing: string[]): ConflictCheckInput {
  const opt = (v: string) => (v.trim() ? v.trim() : undefined);
  return {
    firstName: identity.firstName.trim(),
    lastName: identity.lastName.trim(),
    alienNumber: opt(identity.alienNumber),
    dateOfBirth: opt(identity.dateOfBirth),
    email: opt(identity.email),
    primaryPhone: opt(identity.primaryPhone),
    opposingParties: opposing.length > 0 ? opposing : undefined,
  };
}

function StepRail({ step }: { step: number }) {
  return (
    <div className="intake-steps" role="list" aria-label="Intake steps">
      {STEPS.map((label, i) => (
        <div
          key={label}
          role="listitem"
          className={"intake-step" + (i === step ? " active" : "") + (i < step ? " done" : "")}
          aria-current={i === step ? "step" : undefined}
        >
          <span className="intake-step-dot">{i < step ? CHECK : i + 1}</span>
          <span className="intake-step-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

function AiSuggestion({
  suggestion,
  name,
  children,
}: {
  suggestion: ClassificationResult;
  name: string | null;
  children?: ReactNode;
}) {
  return (
    <div className="intake-ai-card ai-panel">
      <div className="aicard-head">
        <span className="aicard-title">{name}</span>
        <span className="ai-chip">AI</span>
        {suggestion.confidence != null && (
          <span className="tnum">{Math.round(suggestion.confidence * 100)}%</span>
        )}
      </div>
      {suggestion.reasoning && <div className="intake-ai-reason">{suggestion.reasoning}</div>}
      {children}
    </div>
  );
}

export function IntakeWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(0);
  const [identity, setIdentity] = useState<Identity>(EMPTY_IDENTITY);
  const [description, setDescription] = useState("");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [classification, setClassification] = useState<ClassificationResult | null>(null);
  const [classifyNote, setClassifyNote] = useState<string | null>(null);
  const [opposing, setOpposing] = useState<string[]>([]);
  const [opposingDraft, setOpposingDraft] = useState("");
  const [conflicts, setConflicts] = useState<ConflictCandidate[] | null>(null);
  const [conflictErr, setConflictErr] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [created, setCreated] = useState<CreateIntakeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A deliberate click on the template list; the classifier never overrides it.
  const manualPickRef = useRef(false);

  const templates = useQuery({
    queryKey: ["practice-areas", "list"],
    queryFn: listIntakeTemplates,
    staleTime: 5 * 60 * 1000,
  });

  const check = useMutation({
    mutationKey: ["intake", "conflict-check"],
    mutationFn: checkConflicts,
    // The waiver dies the moment a re-check is attempted, not when it
    // lands - a failed re-run must not leave the old waiver armed.
    onMutate: () => setAcknowledged(false),
    onSuccess: (candidates) => {
      setConflicts(candidates);
      setConflictErr(null);
    },
    onError: (err) =>
      setConflictErr(err instanceof Error ? err.message : "The conflict check failed."),
  });

  const classify = useMutation({
    mutationKey: ["intake", "classify"],
    mutationFn: classifyDescription,
    onSuccess: (result) => {
      setClassification(result);
      setClassifyNote(null);
      // Pre-pick only a confident suggestion; a re-run replaces the
      // previous auto-pick, a manual choice always wins.
      if (!manualPickRef.current && result.templateId && (result.confidence ?? 0) >= 0.7) {
        setTemplateId(result.templateId);
      }
    },
    onError: (err) =>
      setClassifyNote(
        err instanceof Error ? err.message : "The classifier is unavailable - pick manually.",
      ),
  });

  const create = useMutation({
    mutationKey: ["intake", "create"],
    mutationFn: createIntakeMatter,
    onSuccess: async (result) => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["matters"] }),
        queryClient.invalidateQueries({ queryKey: ["clients"] }),
        queryClient.invalidateQueries({ queryKey: ["firm", "counts"] }),
      ]);
      setCreated(result);
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : "The matter could not be created."),
  });

  const runCheck = () => check.mutate(conflictPayload(identity, opposing));
  const runLabel = conflicts === null ? "Check for conflicts" : "Re-run conflict check";

  // The review step always opens on a verdict as of now, not as of three
  // steps ago - this is also what makes the check impossible to skip.
  useEffect(() => {
    if (step === 3) runCheck();
  }, [step]);

  const criticals = useMemo(
    () => (conflicts ?? []).filter((c) => c.severity === "critical").length,
    [conflicts],
  );

  const templateName = (id: string | null | undefined) =>
    templates.data?.find((t) => t.id === id)?.name ?? id ?? null;

  // Any edit to a checked field outdates the verdict and its waiver.
  const staleVerdict = () => {
    setConflicts(null);
    setConflictErr(null);
    setAcknowledged(false);
  };

  const pickTemplate = (id: string | null) => {
    manualPickRef.current = true;
    setTemplateId(id);
  };

  const goTo = (next: number) => {
    setError(null);
    setStep(next);
  };

  const submit = () => {
    if (criticals > 0 && !acknowledged) {
      setError("Acknowledge the critical conflict(s) above, or resolve them first.");
      return;
    }
    const opt = (v: string) => (v.trim() ? v.trim() : undefined);
    create.mutate({
      client: {
        firstName: identity.firstName.trim(),
        lastName: identity.lastName.trim(),
        alienNumber: opt(identity.alienNumber),
        dateOfBirth: opt(identity.dateOfBirth),
        email: opt(identity.email),
        primaryPhone: opt(identity.primaryPhone),
        preferredLanguage: opt(identity.preferredLanguage),
      },
      description: description.trim(),
      practiceAreaTemplateId: templateId,
      opposingParties: opposing.length > 0 ? opposing : undefined,
      acknowledgedConflicts: acknowledged || undefined,
    });
  };

  const reset = () => {
    setStep(0);
    setIdentity(EMPTY_IDENTITY);
    setDescription("");
    setTemplateId(null);
    setClassification(null);
    setClassifyNote(null);
    setOpposing([]);
    setOpposingDraft("");
    setConflicts(null);
    setConflictErr(null);
    setAcknowledged(false);
    setCreated(null);
    setError(null);
    manualPickRef.current = false;
  };

  const field = (key: keyof Identity) => ({
    value: identity[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setIdentity((cur) => ({ ...cur, [key]: e.target.value }));
      if (key !== "preferredLanguage") staleVerdict();
    },
  });

  const addOpposing = () => {
    const name = opposingDraft.trim();
    if (!name || opposing.length >= MAX_OPPOSING) return;
    setOpposing((cur) => (cur.includes(name) ? cur : [...cur, name]));
    setOpposingDraft("");
    staleVerdict();
  };

  const canNext =
    step === 0
      ? identity.firstName.trim().length > 0 && identity.lastName.trim().length > 0
      : step === 1
        ? description.trim().length > 0
        : true;

  if (created) {
    const c = created.client;
    const applied = created.practiceAreaTemplateId;
    // With a templateId the model answered and its words are AI content;
    // without one the reasoning is the server's own sentence.
    const note = applied ? null : created.classification;
    const suggestion = note?.templateId ? note : null;
    const unsetNote = suggestion
      ? "The suggestion below was not confident enough to apply."
      : note?.reasoning;
    const warnings = created.conflicts.filter((x) => x.severity === "warning").length;
    return (
      <main className="cc-main">
        <div className="cc-body">
          <div className="cc-index-head">
            <span className="title">Matter created</span>
          </div>
          <div className="cc-card" style={{ padding: 18, maxWidth: 640 }}>
            <div className="intake-done-row">
              <span className="intake-done-label">Client</span>
              <span>
                {c ? `${c.firstName} ${c.lastName}` : "(no client record)"}
                {c && (
                  <span style={{ color: "var(--muted)" }}>
                    {" "}
                    {c.wasCreated
                      ? "(new client)"
                      : `(attached to an existing client - ${MATCH_WORDS[c.matchType] ?? c.matchType})`}
                  </span>
                )}
              </span>
            </div>
            <div className="intake-done-row">
              <span className="intake-done-label">Matter</span>
              <span className="tnum-mono">{matterRouteId(created.matter)}</span>
            </div>
            <div className="intake-done-row">
              <span className="intake-done-label">Practice area</span>
              <span>
                {applied ? templateName(applied) : "(unset - staff will assign later)"}
                {unsetNote && <span style={{ color: "var(--muted)" }}> {unsetNote}</span>}
              </span>
            </div>
            {suggestion && (
              <AiSuggestion suggestion={suggestion} name={templateName(suggestion.templateId)} />
            )}
            {warnings > 0 && (
              <div className="intake-done-row">
                <span className="intake-done-label">Conflicts</span>
                <span style={{ color: "var(--warn-600)" }}>
                  {warnings} warning-level candidate{warnings === 1 ? "" : "s"} recorded - review on
                  the matter if needed.
                </span>
              </div>
            )}
            <div className="cc-form-actions" style={{ marginTop: 14 }}>
              <button type="button" className="cc-link-btn" onClick={reset}>
                Start another intake
              </button>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="cc-btn-primary"
                onClick={() => navigate(`/matter/${matterRouteId(created.matter)}`)}
              >
                Open the matter
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="cc-main">
      <div className="cc-body">
        <div className="cc-index-head">
          <span className="title">New client intake</span>
          <span className="blurb">
            Conflict-checked intake - four steps from a name to a matter.
          </span>
        </div>

        <StepRail step={step} />

        {step === 0 && (
          <div className="cc-form-panel">
            <div className="cc-form-row">
              <label className="cc-form-field" style={{ flex: 1, minWidth: 200 }}>
                <span className="cc-form-label">First name</span>
                <input
                  className="cc-field"
                  aria-label="First name"
                  maxLength={120}
                  {...field("firstName")}
                />
              </label>
              <label className="cc-form-field" style={{ flex: 1, minWidth: 200 }}>
                <span className="cc-form-label">Last name</span>
                <input
                  className="cc-field"
                  aria-label="Last name"
                  maxLength={120}
                  {...field("lastName")}
                />
              </label>
            </div>
            <div className="cc-form-row">
              <label className="cc-form-field" style={{ flex: 1, minWidth: 200 }}>
                <span className="cc-form-label">A-Number</span>
                <input
                  className="cc-field tnum-mono"
                  aria-label="A-Number"
                  placeholder="123-456-789"
                  maxLength={40}
                  {...field("alienNumber")}
                />
                <span className="cc-form-hint">USCIS Alien Registration Number - optional.</span>
              </label>
              <label className="cc-form-field" style={{ width: 180 }}>
                <span className="cc-form-label">Date of birth</span>
                <input
                  className="cc-field"
                  aria-label="Date of birth"
                  type="date"
                  {...field("dateOfBirth")}
                />
              </label>
              <label className="cc-form-field" style={{ width: 200 }}>
                <span className="cc-form-label">Preferred language</span>
                <select
                  className="cc-field"
                  aria-label="Preferred language"
                  {...field("preferredLanguage")}
                >
                  <option value="">Not set</option>
                  {LANGUAGES.map((code) => (
                    <option key={code} value={code}>
                      {languageName(code)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="cc-form-row">
              <label className="cc-form-field" style={{ flex: 1, minWidth: 200 }}>
                <span className="cc-form-label">Phone</span>
                <input
                  className="cc-field"
                  aria-label="Phone"
                  placeholder="215-555-0100"
                  maxLength={40}
                  {...field("primaryPhone")}
                />
              </label>
              <label className="cc-form-field" style={{ flex: 1, minWidth: 240 }}>
                <span className="cc-form-label">Email</span>
                <input
                  className="cc-field"
                  aria-label="Email"
                  maxLength={200}
                  {...field("email")}
                />
              </label>
            </div>
            <ConflictSection
              candidates={conflicts}
              checking={check.isPending}
              error={conflictErr}
              onRun={runCheck}
              runLabel={runLabel}
              disabled={!canNext}
            />
          </div>
        )}

        {step === 1 && (
          <div className="cc-form-panel">
            <label className="cc-form-field">
              <span className="cc-form-label">Describe the matter</span>
              <textarea
                className="cc-field intake-textarea"
                aria-label="Matter description"
                rows={6}
                maxLength={8000}
                placeholder="What happened, when, who is involved, and what the client is asking the firm to do..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <span className="cc-form-hint">
                What happened, what the client needs, what relief the firm is pursuing.
              </span>
            </label>

            <div className="intake-classify-bar">
              <button
                type="button"
                className="cc-link-btn"
                disabled={!description.trim() || classify.isPending}
                onClick={() => classify.mutate(description.trim())}
              >
                {classify.isPending ? "Classifying..." : "Auto-classify with AI"}
              </button>
              {classifyNote && <span className="cc-form-hint">{classifyNote}</span>}
            </div>

            {classification?.templateId && (
              <AiSuggestion
                suggestion={classification}
                name={templateName(classification.templateId)}
              >
                {(classification.alternatives ?? []).length > 0 && (
                  <div className="intake-ai-alts">
                    {(classification.alternatives ?? []).map(
                      (a) =>
                        a.templateId && (
                          <button
                            key={a.templateId}
                            type="button"
                            className="cc-link-btn"
                            onClick={() => pickTemplate(a.templateId ?? null)}
                          >
                            {templateName(a.templateId)}
                            {a.confidence != null ? ` ${Math.round(a.confidence * 100)}%` : ""}
                          </button>
                        ),
                    )}
                  </div>
                )}
              </AiSuggestion>
            )}

            <div className="intake-tpl-list" role="radiogroup" aria-label="Practice area template">
              <button
                type="button"
                role="radio"
                aria-checked={templateId === null}
                className={"intake-tpl-row" + (templateId === null ? " picked" : "")}
                onClick={() => pickTemplate(null)}
              >
                <span>(unset - staff will assign later)</span>
              </button>
              {templates.isLoading && (
                <SkeletonGroup label="Loading practice areas">
                  <Skeleton height={30} />
                  <Skeleton height={30} />
                  <Skeleton height={30} />
                  <Skeleton height={30} />
                </SkeletonGroup>
              )}
              {(templates.data ?? []).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={templateId === t.id}
                  className={"intake-tpl-row" + (templateId === t.id ? " picked" : "")}
                  onClick={() => pickTemplate(t.id)}
                >
                  <span>{t.name}</span>
                  <span style={{ color: "var(--muted)" }}>{t.parent ?? "other"}</span>
                </button>
              ))}
              {templates.isSuccess && (templates.data ?? []).length === 0 && (
                <div className="cc-form-hint">
                  No practice-area templates are configured - the matter will be created unset.
                </div>
              )}
              {templates.isError && (
                <div className="cc-form-hint">
                  Could not load the template list - the matter can still be created unset.
                </div>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="cc-form-panel">
            <p className="intake-prose">
              Opposing parties are optional but recommended: checking them against the client base
              is the only way to surface RPC 1.7 / 1.9 conflicts from prior representation.
            </p>
            <div className="cc-form-row">
              <label className="cc-form-field" style={{ flex: 1, minWidth: 240 }}>
                <span className="cc-form-label">Opposing party</span>
                <input
                  className="cc-field"
                  aria-label="Opposing party name"
                  maxLength={200}
                  value={opposingDraft}
                  onChange={(e) => setOpposingDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addOpposing();
                    }
                  }}
                />
              </label>
              <div className="cc-form-field" style={{ justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="cc-link-btn"
                  disabled={!opposingDraft.trim() || opposing.length >= MAX_OPPOSING}
                  onClick={addOpposing}
                >
                  Add
                </button>
              </div>
            </div>
            {opposing.length > 0 && (
              <div className="intake-chips">
                {opposing.map((name) => (
                  <span key={name} className="intake-chip">
                    {name}
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      onClick={() => {
                        setOpposing((cur) => cur.filter((n) => n !== name));
                        staleVerdict();
                      }}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
            <ConflictSection
              candidates={conflicts}
              checking={check.isPending}
              error={conflictErr}
              onRun={runCheck}
              runLabel={runLabel}
            />
          </div>
        )}

        {step === 3 && (
          <div className="cc-form-panel">
            <div className="intake-summary">
              <div className="intake-done-row">
                <span className="intake-done-label">Client</span>
                <span>
                  {identity.firstName.trim()} {identity.lastName.trim()}
                </span>
              </div>
              {identity.alienNumber.trim() && (
                <div className="intake-done-row">
                  <span className="intake-done-label">A-Number</span>
                  <span className="tnum-mono">{identity.alienNumber.trim()}</span>
                </div>
              )}
              {identity.dateOfBirth && (
                <div className="intake-done-row">
                  <span className="intake-done-label">Date of birth</span>
                  <span className="tnum">{identity.dateOfBirth}</span>
                </div>
              )}
              {identity.primaryPhone.trim() && (
                <div className="intake-done-row">
                  <span className="intake-done-label">Phone</span>
                  <span>{identity.primaryPhone.trim()}</span>
                </div>
              )}
              {identity.email.trim() && (
                <div className="intake-done-row">
                  <span className="intake-done-label">Email</span>
                  <span>{identity.email.trim()}</span>
                </div>
              )}
              <div className="intake-done-row">
                <span className="intake-done-label">Practice area</span>
                <span>
                  {templateId ? templateName(templateId) : "(unset - staff will assign later)"}
                </span>
              </div>
              {opposing.length > 0 && (
                <div className="intake-done-row">
                  <span className="intake-done-label">Opposing</span>
                  <span>{opposing.join("; ")}</span>
                </div>
              )}
            </div>
            <div className="intake-description-block">{description.trim()}</div>
            <ConflictSection
              candidates={conflicts}
              checking={check.isPending}
              error={conflictErr}
              onRun={runCheck}
              runLabel={runLabel}
              acknowledged={acknowledged}
              onAcknowledge={(v) => {
                setAcknowledged(v);
                setError(null);
              }}
            />
          </div>
        )}

        <div className="cc-form-actions intake-footer">
          {step > 0 && (
            <button type="button" className="cc-link-btn" onClick={() => goTo(step - 1)}>
              Back
            </button>
          )}
          <span style={{ flex: 1 }} />
          {step < 3 ? (
            <button
              type="button"
              className="cc-btn-primary"
              disabled={!canNext}
              onClick={() => goTo(step + 1)}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className="cc-btn-primary"
              disabled={create.isPending || check.isPending}
              onClick={submit}
            >
              {create.isPending ? "Creating..." : "Create client + matter"}
            </button>
          )}
        </div>

        {error && (
          <div role="alert" className="cc-form-error">
            {error}
          </div>
        )}
      </div>
    </main>
  );
}
